<ng-container *ngIf="claim$ | async as claim">
  <ng-container *vsLet="canManage$ | async as canManage">
    <ng-container *vsLet="loading$ | async as loading">
      <ng-container *vsLet="meta$ | async as meta">
        <vs-card theme="default" class="block @container">
          <vs-card-header
            class="[&>div]:!flex-wrap [&>div>div:first-child]:!min-w-[200px] [&>div>div:first-child>div]:!flex-nowrap !py-3 @xl:!py-4 !px-4 @xl:!px-5"
            [config]="{ title: 'Attachments', subtitle: 'Manage and view all attachments linked to this claim.' }"
          >
            <vs-badge
              *vsLet="(meta?.totalItems ?? null) as totalItems"
              slot="after-title"
              [class.italic]="totalItems === null"
              [class.animate-pulse]="totalItems === null"
            >
              {{ totalItems === null ? 'loading' : (totalItems | number) + ' attachments' }}
            </vs-badge>

            <div slot="end" class="flex gap-2" *ngIf="canManage === true">
              <button vs-button theme="primary" class="!w-full" (click)="upload_dialog.open(claim.id)">
                <vs-icon class="hidden sm:inline-block" name="upload-01"></vs-icon>
                Upload
              </button>

              <button vs-icon-button appearance="outline" class="aspect-square" [vsMenuTriggerFor]="linkMenu">
                <vs-icon name="link-external-01" size="20"></vs-icon>

                <ng-template #linkMenu>
                  <vs-menu *vsLet="linkButtonInfo$ | async as linkButtonInfo" class="mt-2">
                    <button *ngFor="let link of linkButtonInfo" vs-menu-item (click)="copyLinkToClipboard(link)">
                      {{ link.text }}
                    </button>
                  </vs-menu>
                </ng-template>
              </button>
            </div>
          </vs-card-header>

          <vs-card-content class="!p-0 @container">
            <div #container class="relative">
              <div
                *vsLet="expandedItem$ | async as expandedItem"
                class="overflow-auto scrollbar-thin"
                [class.pointer-events-none]="loading"
                [class.!overflow-hidden]="(!loading && meta?.totalItems === 0)"
              >
                <div id="table">
                  <!-- Header -->
                  <div id="header">
                    <div class="cell">
                      <p>Uploaded</p>
                    </div>

                    <div class="cell">
                      <p>Uploaded by</p>
                    </div>

                    <div class="cell">
                      <p>File type</p>
                    </div>

                    <div class="cell">
                      <p>Title</p>
                    </div>

                    <div class="cell">
                      <p>Visibility</p>
                    </div>

                    <div class="cell"></div>
                  </div>

                  <ng-container *ngFor="let item of attachments$ | async; trackBy: trackById; let even = even">
                    <div class="row" [attr.data-even]="even">
                      <!-- Uploaded -->
                      <div
                        class="cell col-start-1"
                        tooltipStyle="light"
                        tooltipOpenDelay="150"
                        [vsTooltip]="{ title: 'Uploaded on', subtitle: (item.created | date : 'medium') ?? '' }"
                        (click)="expand(item)"
                      >
                        <p>{{ item.created | date : 'shortDate' }}</p>
                      </div>

                      <!-- Uploaded by -->
                      <div
                        class="cell"
                        tooltipStyle="light"
                        tooltipOpenDelay="150"
                        [vsTooltip]="item.createdBy ? { title: 'Uploaded by', subtitle: '<p>' + item.createdBy.displayName + '</p><p>' + item.createdBy.email + '</p>' }  : undefined"
                        (click)="expand(item)"
                      >
                        <p *ngIf="item.createdBy" class="max-w-xs">{{ item.createdBy.displayName }}</p>
                        <p *ngIf="!item.createdBy" class="text-gray-400 italic">System</p>
                      </div>

                      <!-- File type -->
                      <div
                        class="cell"
                        tooltipStyle="light"
                        tooltipOpenDelay="150"
                        [vsTooltip]="{ title: 'File type', subtitle: item.mimeType }"
                        (click)="expand(item)"
                      >
                        <ng-container *vsLet="item.mimeType === 'application/pdf' as isPDF">
                          <ng-container *vsLet="item.mimeType.startsWith('image') as isImage">
                            <vs-badge *ngIf="isPDF" theme="danger">pdf</vs-badge>
                            <vs-badge *ngIf="isImage" theme="info">image</vs-badge>
                            <vs-badge *ngIf="!isPDF && !isImage" theme="default">other</vs-badge>
                          </ng-container>
                        </ng-container>
                      </div>

                      <!-- Title -->
                      <div
                        class="cell"
                        tooltipStyle="light"
                        tooltipOpenDelay="150"
                        [vsTooltip]="{ title: 'Title & file name', subtitle: '<p>' + item.title + '</p><p>' + item.originalFilename + '</p>' }"
                        (click)="expand(item)"
                      >
                        <p class="max-w-xs">{{ item.title }}</p>
                      </div>

                      <!-- Visibility -->
                      <div class="cell gap-1" (click)="expand(item)">
                        <vs-badge theme="primary">Staff</vs-badge>
                        <vs-badge *ngIf="item.feed.includes('customer')" theme="default">Customer</vs-badge>
                      </div>

                      <!-- Actions -->
                      <div class="cell sticky right-0 z-10 gap-3">
                        <!-- Open -->
                        <a
                          target="_blank"
                          class="group py-2 px-3 -my-2 -mx-3 flex items-center"
                          [href]="item.downloadUrl + '&embedded=true'"
                          (click)="$event.stopImmediatePropagation();"
                        >
                          <vs-icon
                            size="16"
                            name="link-external-01"
                            class="transition-all group-hover:scale-[1.2] group-active:scale-95 text-gray-500 group-hover:text-info-600"
                          ></vs-icon>
                        </a>

                        <!-- Expand/Collapse -->
                        <button
                          class="group py-2 px-3 -my-2 -mx-3 flex items-center"
                          (click)="$event.stopImmediatePropagation(); expand(item)"
                        >
                          <vs-icon
                            name="chevron-down"
                            size="16"
                            class="transition-all group-hover:scale-[1.2] group-active:scale-95 text-gray-500 group-hover:text-gray-600"
                            [class.-rotate-180]="expandedItem?.id === item.id"
                          ></vs-icon>
                        </button>
                      </div>
                    </div>

                    <!-- Contents -->
                    <div
                      class="sticky left-0 grid transition-all will-change-transform w-0"
                      [style.grid-template-rows]="item.id === expandedItem?.id ? '1fr' : '0fr'"
                    >
                      <div class="overflow-hidden opacity-0 pointer-events-none">
                        <div
                          class="max-h-96 border-b px-6 py-4 overflow-visible"
                          [style.width.px]="container.offsetWidth"
                        >
                          <ng-container
                            *ngTemplateOutlet="item_preview; context: { item: item, skipDownload: true }"
                          ></ng-container>
                        </div>
                      </div>

                      <div
                        class="absolute top-0 left-0 grid transition-all"
                        [style.width.px]="container.offsetWidth"
                        [style.grid-template-rows]="item.id === expandedItem?.id ? '1fr' : '0fr'"
                      >
                        <div class="overflow-hidden">
                          <div class="max-h-96 border-b border-gray-200 px-6 py-4 overflow-auto scrollbar-thin">
                            <ng-container *ngTemplateOutlet="item_preview; context: { item: item }"></ng-container>
                          </div>
                        </div>
                      </div>
                    </div>
                  </ng-container>
                </div>
              </div>

              <!-- Loading State -->
              <div *ngIf="loading" class="absolute inset-0 bg-gray-50/25 z-20 overflow-hidden">
                <div class="relative w-full h-[3px] mt-[32px]">
                  <div class="absolute inset-0 bg-primary-100"></div>
                  <div class="indeterminate-progress-bar-value absolute w-full h-full bg-primary-400"></div>
                </div>
              </div>

              <!-- Empty State -->
              <div
                *vsLet="(!loading && meta?.totalItems === 0) as empty"
                class="text-center py-8 overflow-hidden transition-opacity opacity-0"
                [class.hidden]="!!meta?.totalItems"
                [class.!opacity-100]="empty"
              >
                <h1 class="whitespace-nowrap font-semibold text-gray-700">No attachments linked to the claim</h1>
                <p class="whitespace-nowrap text-sm text-gray-700">Upload a file to get started</p>
              </div>
            </div>

            <!-- Pagination -->
            <ng-container *vsLet="pagination$ | async as pagination">
              <div *ngIf="(meta?.totalPages ?? 0) > 1" class="px-4 py-2">
                <div class="flex gap-2 justify-between @md:justify-end">
                  <button
                    vs-button
                    size="sm"
                    theme="default"
                    appearance="clear"
                    class="!w-8 aspect-square"
                    [disabled]="pagination?.page === 1"
                    (click)="pagination$.next({ page: (pagination?.page ?? 2) - 1, limit: 10 })"
                  >
                    <vs-icon name="chevron-left" size="18"></vs-icon>
                  </button>

                  <div class="flex">
                    <ng-container *ngFor="let page of pages$ | async">
                      <button
                        vs-button
                        size="sm"
                        appearance="clear"
                        class="!w-8 aspect-square"
                        [theme]="pagination?.page === page ? 'primary' : 'default'"
                        (click)="pagination$.next({ page: page, limit: 10 })"
                      >
                        <span [class.text-themed-500]="pagination?.page === page">{{ page }}</span>
                      </button>
                    </ng-container>
                  </div>

                  <button
                    vs-button
                    size="sm"
                    theme="default"
                    appearance="clear"
                    class="!w-8 aspect-square"
                    [disabled]="pagination?.page === meta?.totalPages"
                    (click)="pagination$.next({ page: (pagination?.page ?? 0) + 1, limit: 10 })"
                  >
                    <vs-icon name="chevron-right" size="18"></vs-icon>
                  </button>
                </div>
              </div>
            </ng-container>
          </vs-card-content>
        </vs-card>

        <ng-template #item_preview let-item="item" let-skipDownload="skipDownload">
          <div class="flex items-start gap-4">
            <!-- Preview -->
            <div
              *ngIf="item.downloadUrl"
              class="w-32 aspect-square border border-gray-200 rounded-md overflow-hidden relative"
            >
              <ng-container *vsLet="item.mimeType === 'application/pdf' as isPDF">
                <ng-container *vsLet="item.mimeType.startsWith('image') as isImage">
                  <!-- Content -->
                  <div *ngIf="!skipDownload" class="w-full h-full select-none">
                    <iframe
                      *ngIf="isPDF"
                      scrolling="no"
                      class="w-full h-full overflow-hidden no-scrollbar"
                      [src]="item.downloadUrl + '&embedded=true&navpanes=0&scrollbar=0#toolbar=0' | safe : 'resourceUrl'"
                      (load)="progress_bar.style.display = 'none'; placeholder.style.display = 'none'"
                    ></iframe>

                    <img
                      *ngIf="isImage"
                      class="w-full h-full object-cover"
                      [src]="item.downloadUrl + '&embedded=true'"
                      (load)="progress_bar.style.display = 'none'; placeholder.style.display = 'none'"
                    />
                  </div>

                  <!-- Placeholder -->
                  <div #placeholder class="bg-base absolute inset-0 flex items-center justify-center select-none">
                    <p *ngIf="isPDF" class="text-gray-300 font-extrabold">PDF</p>

                    <vs-icon *ngIf="isImage" name="image-03" size="24" class="text-gray-300"></vs-icon>

                    <vs-icon
                      *ngIf="!isPDF && !isImage"
                      size="24"
                      class="text-gray-300"
                      name="file-question-03"
                    ></vs-icon>
                  </div>

                  <!-- Progress bar -->
                  <div
                    #progress_bar
                    class="indeterminate-progress-bar-value absolute w-full h-0.5 bg-primary-400 bottom-0"
                    [style.display.none]="!isPDF && !isImage"
                  ></div>
                </ng-container>
              </ng-container>
            </div>

            <!-- Info -->
            <div class="flex-1 text-sm text-gray-900 grid grid-cols-[auto_1fr] gap-y-2 gap-x-4">
              <!-- Title -->
              <ng-container>
                <p class="font-medium whitespace-nowrap">Title</p>
                <div class="flex overflow-hidden items-center pr-1">
                  <p class="truncate mr-1">{{ item.title }}</p>

                  <div *ngIf="canManage" class="ml-auto flex items-center gap-3">
                    <button
                      class="group flex items-center"
                      (click)="edit_dialog.open({ claim: claim, document: item })"
                    >
                      <vs-icon
                        size="16"
                        name="edit-01"
                        class="transition-all group-hover:scale-[1.2] group-active:scale-95 text-gray-700 group-hover:text-gray-600"
                      ></vs-icon>
                    </button>

                    <button
                      class="group flex items-center"
                      (click)="deletingItemId = item.id; delete_item_dialog.open()"
                    >
                      <vs-icon
                        size="16"
                        name="trash-01"
                        class="transition-all group-hover:scale-[1.2] group-active:scale-95 text-gray-700 group-hover:text-danger-600"
                      ></vs-icon>
                    </button>
                  </div>
                </div>
              </ng-container>

              <div class="col-span-full h-px bg-gray-200"></div>

              <!-- Description -->
              <ng-container>
                <p class="font-medium whitespace-nowrap">Description</p>
                <p class="line-clamp-4">{{ item.description }}</p>
              </ng-container>

              <div class="col-span-full h-px bg-gray-200"></div>

              <p class="font-medium whitespace-nowrap">File type</p>
              <p class="truncate">{{ item.mimeType }}</p>

              <div class="col-span-full h-px bg-gray-200"></div>

              <p class="font-medium whitespace-nowrap">Uploaded by</p>
              <p class="truncate">{{ item.createdBy.displayName }}</p>

              <div class="col-span-full h-px bg-gray-200"></div>

              <p class="font-medium whitespace-nowrap">Uploaded on</p>
              <p class="truncate">{{ item.created | date : 'medium' }}</p>

              <div class="col-span-full h-px bg-gray-200"></div>

              <p class="font-medium whitespace-nowrap">Visible to</p>
              <div>
                <vs-badge theme="primary">Staff</vs-badge>
                <vs-badge *ngIf="item.feed.includes('customer')" class="ml-1" theme="default">Customer</vs-badge>
              </div>
            </div>
          </div>
        </ng-template>

        <ws-upload-claim-document-dialog #upload_dialog [isCustomerPortal]="false"></ws-upload-claim-document-dialog>

        <ws-edit-claim-document-dialog
          #edit_dialog
          [isCustomerPortal]="false"
          (updated)="refresh()"
        ></ws-edit-claim-document-dialog>

        <vs-dialog #delete_item_dialog [config]="{ dialogContainerClass: 'max-w-sm' }">
          <vs-dialog-header
            [config]="{
      mode: 'simple',
      showDivider: false,
      hideCloseButton: true,

      icon: 'file-minus-02',
      iconTheme: 'danger',

      title: 'Remove document',
      subtitle: 'Delete this document. Careful, deleted documents cannot be restored.'
    }"
          ></vs-dialog-header>

          <div class="mt-4 flex flex-wrap gap-2 items-center">
            <button
              vs-button
              class="flex-1"
              appearance="outline"
              (click)="(deletingItemId = null); delete_item_dialog.close()"
            >
              Cancel
            </button>

            <button vs-button class="flex-1" theme="danger" (click)="deleteItem(); delete_item_dialog.close()">
              Remove
            </button>
          </div>
        </vs-dialog>
      </ng-container>
    </ng-container>
  </ng-container>
</ng-container>
